/**
 * Navigation Utilities - SINGLE SOURCE OF TRUTH
 *
 * All URL parsing, building, and navigation operations MUST go through this file.
 * Views must NOT import this file directly - use onNavigateLegacy prop instead.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import { v4 as uuidv4 } from 'uuid';

// Navigation version for telemetry tracking across rollouts
export const NAVIGATION_VERSION = '2.3';

// =============================================================================
// URL Building & Parsing
// =============================================================================

/**
 * Build hash from section, view, and optional param
 * @param {string} section - Section ID (e.g., 'production')
 * @param {string} view - View ID (e.g., 'pa-queue')
 * @param {string} [param] - Optional param (e.g., quote number for project-page)
 * @returns {string} Hash string (e.g., '#/production/project-page/Q12345')
 */
export function buildHash(section, view, param) {
  if (param) {
    return `#/${section}/${view}/${param}`;
  }
  return `#/${section}/${view}`;
}

/**
 * Parse hash into { section, view, param }
 * @param {string} hash - Hash string (e.g., '#/production/project-page/Q12345')
 * @returns {{ section: string|null, view: string|null, param: string|null }|null} Parsed route or null
 */
export function parseHash(hash) {
  if (!hash || !hash.startsWith('#/')) return null;
  const parts = hash.slice(2).split('/'); // Remove "#/" prefix
  if (parts.length < 1) return null;
  return {
    section: parts[0] || null,
    view: parts[1] || null,
    param: parts[2] || null,
  };
}

// =============================================================================
// Legacy Aliases
// =============================================================================

/**
 * Legacy URL aliases for backwards compatibility
 */
const LEGACY_ALIASES = {
  '#pa-queue': { section: 'production', view: 'pa-queue' },
  '#assembly': { section: 'production', view: 'assembly' },
  '#shipping': { section: 'production', view: 'shipping' },
  '#projects': { section: 'production', view: 'projects' },
  '#printers': { section: 'production', view: 'printers' },
  '#reports': { section: 'insights', view: 'reports' },
  '#dashboards': { section: 'insights', view: 'dashboards' },
  '#admin': { section: 'administration', view: 'users' },
  '#my-work': { section: 'time', view: 'my-work' },
  '#team': { section: 'time', view: 'team-hours' },
  '#history': { section: 'time', view: 'history' },
  '#schedule-board': { section: 'scheduling', view: 'board' },
};

/**
 * Resolve legacy hash aliases to new routes
 * @param {string} hash - Legacy hash
 * @returns {{ section: string, view: string }|null} Resolved route or null
 */
export function resolveLegacyAlias(hash) {
  return LEGACY_ALIASES[hash] || null;
}

// =============================================================================
// Legacy Action Map (for internal navigation from views)
// =============================================================================

/**
 * Map old showXXX actions to new navigation
 */
export const LEGACY_ACTION_MAP = {
  showPAQueue: ['production', 'pa-queue'],
  showAssemblyQueue: ['production', 'assembly'],
  showShippingQueue: ['production', 'shipping'],
  showProjects: ['production', 'projects'],
  showTeamView: ['production', 'team-status'],
  showPrinters: ['production', 'printers'],
  showPMCenter: ['production', 'pm-center'],
  showWorkOrderReview: ['production', 'wo-generator'],
  showScheduleBoard: ['scheduling', 'board'],
  showMyWork: ['time', 'my-work'],
  showTeamHours: ['time', 'team-hours'],
  showSessionHistory: ['time', 'history'],
  showReports: ['insights', 'reports'],
  showDashboards: ['insights', 'dashboards'],
  showAdminUsers: ['administration', 'users'],
  showAdminTickets: ['administration', 'tickets'],
  showDataQuality: ['administration', 'data-quality'],
  showFeedback: ['administration', 'feedback'],
  showNotifications: ['administration', 'notifications'],
  showWorkInstructions: ['administration', 'work-instructions'],
};

/**
 * Resolve legacy action to section/view
 * @param {string} action - Legacy action name (e.g., 'showPAQueue')
 * @returns {[string, string]|null} [section, view] tuple or null
 */
export function resolveLegacyAction(action) {
  return LEGACY_ACTION_MAP[action] || null;
}

// =============================================================================
// Navigation
// =============================================================================

/**
 * Navigate to section/view (updates URL, triggers hashchange)
 * @param {string} section - Section ID
 * @param {string} view - View ID
 * @param {string} [param] - Optional param (e.g., quote number)
 */
export function navigateToHash(section, view, param) {
  window.location.hash = buildHash(section, view, param);
  // hashchange event will fire and trigger state update
}

/**
 * Get current route from URL
 * @returns {{ section: string|null, view: string|null, param: string|null }|null} Current route
 */
export function getCurrentRoute() {
  const hash = window.location.hash;

  // Check legacy aliases first
  const legacy = resolveLegacyAlias(hash);
  if (legacy) return legacy;

  // Parse standard format
  return parseHash(hash);
}

// =============================================================================
// Telemetry
// =============================================================================

let sessionId = null;
let previousSection = null;
let previousView = null;

/**
 * Get or create session ID
 */
function getSessionId() {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('nav_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('nav_session_id', sessionId);
    }
  }
  return sessionId;
}

/**
 * Get user role string for telemetry
 * @param {object} user - User object with isAdmin and isPM flags
 * @returns {string} Role string
 */
function getUserRole(user) {
  const roles = [];
  if (user?.isAdmin) roles.push('admin');
  if (user?.isPM) roles.push('pm');
  return roles.length > 0 ? roles.join(',') : 'user';
}

/**
 * Emit navigation telemetry event
 * @param {string} type - Event type
 * @param {object} data - Event data
 * @param {object} user - User object for role info
 */
export function emitNavEvent(type, data = {}, user = null) {
  const event = {
    event_id: uuidv4(),
    type,
    timestamp: new Date().toISOString(),
    navigation_version: NAVIGATION_VERSION,
    user_role: getUserRole(user),
    client_version: import.meta.env.VITE_APP_VERSION || 'dev',
    session_id: getSessionId(),
    previous_section: previousSection,
    previous_view: previousView,
    ...data,
  };

  // Update previous state for next event
  if (data.section) previousSection = data.section;
  if (data.view) previousView = data.view;

  // Fire and forget (Phase 1)
  // Note: OK to batch later in Phase 2
  // TODO: Enable when /api/telemetry/events endpoint is created
  // fetch('/api/telemetry/events', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify(event),
  // }).catch(() => {
  //   // Silently fail - telemetry should never break the app
  // });

  // Also log locally for debugging
  if (import.meta.env.DEV) {
    console.debug('[Nav Telemetry]', type, data);
  }
}

// =============================================================================
// Session Storage Helpers
// =============================================================================

const LAST_VIEWS_KEY = 'nav_lastViews';

/**
 * Get last views per section from sessionStorage
 * @returns {Record<string, string>} Map of section -> last view
 */
export function getLastViewsBySection() {
  try {
    const stored = sessionStorage.getItem(LAST_VIEWS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Save last views per section to sessionStorage
 * @param {Record<string, string>} lastViews - Map of section -> last view
 */
export function saveLastViewsBySection(lastViews) {
  try {
    sessionStorage.setItem(LAST_VIEWS_KEY, JSON.stringify(lastViews));
  } catch {
    // Silently fail if sessionStorage is full/unavailable
  }
}
